/**
 * Reversi Game Persistence Service
 *
 * Auto-saves game state to local storage after each move.
 * Follows SnakesLaddersGamePersistence pattern.
 */

import {
  ReversiGameState,
  ReversiGameSettings,
  ReversiDifficulty,
} from '../types';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import { DataSynchronizationService } from '../../services/DataSynchronizationService';

export interface SavedReversiGame {
  gameId: string;
  mode: ReversiGameSettings['mode'];
  gameState: ReversiGameState;
  metadata: {
    aiDifficulty?: ReversiDifficulty;
    startedAt: number;
    lastSavedAt: number;
  };
}

export class ReversiGamePersistence {
  private storage: LocalStorageManager;
  private syncService: DataSynchronizationService | null;
  private readonly STORAGE_KEY = 'reversi_saved_games';
  private readonly AUTO_SAVE_KEY = 'reversi_auto_save';

  constructor(
    storage: LocalStorageManager,
    syncService?: DataSynchronizationService
  ) {
    this.storage = storage;
    this.syncService = syncService || null;
  }

  /** Auto-save the current game state after each move. */
  autoSave(
    mode: ReversiGameSettings['mode'],
    gameState: ReversiGameState,
    metadata: SavedReversiGame['metadata']
  ): boolean {
    try {
      const savedGame: SavedReversiGame = {
        gameId: 'auto_save',
        mode,
        gameState,
        metadata: { ...metadata, lastSavedAt: Date.now() },
      };
      localStorage.setItem(this.AUTO_SAVE_KEY, JSON.stringify(savedGame));
      return true;
    } catch (error) {
      console.error('Failed to auto-save reversi game:', error);
      return false;
    }
  }

  /** Load the auto-saved game state. */
  loadAutoSave(): SavedReversiGame | null {
    try {
      const data = localStorage.getItem(this.AUTO_SAVE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Failed to load reversi auto-save:', error);
      return null;
    }
  }

  /** Clear the auto-saved game state. */
  clearAutoSave(): boolean {
    try {
      localStorage.removeItem(this.AUTO_SAVE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to clear reversi auto-save:', error);
      return false;
    }
  }

  /** Check if an auto-save exists. */
  hasAutoSave(): boolean {
    return this.loadAutoSave() !== null;
  }

  /** Save a game with a specific ID. */
  saveGame(
    gameId: string,
    mode: ReversiGameSettings['mode'],
    gameState: ReversiGameState,
    metadata: SavedReversiGame['metadata']
  ): boolean {
    try {
      const savedGame: SavedReversiGame = {
        gameId,
        mode,
        gameState,
        metadata: { ...metadata, lastSavedAt: Date.now() },
      };

      const games = this.loadAllGames();
      const existingIndex = games.findIndex((g) => g.gameId === gameId);

      if (existingIndex >= 0) {
        games[existingIndex] = savedGame;
      } else {
        games.push(savedGame);
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(games));
      return true;
    } catch (error) {
      console.error('Failed to save reversi game:', error);
      return false;
    }
  }

  /** Load a saved game by ID. */
  loadGame(gameId: string): SavedReversiGame | null {
    try {
      const games = this.loadAllGames();
      return games.find((g) => g.gameId === gameId) || null;
    } catch (error) {
      console.error('Failed to load reversi game:', error);
      return null;
    }
  }

  /** Load all saved games. */
  loadAllGames(): SavedReversiGame[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Failed to load reversi games:', error);
      return [];
    }
  }

  /** Delete a saved game by ID. */
  deleteGame(gameId: string): boolean {
    try {
      const games = this.loadAllGames();
      const filtered = games.filter((g) => g.gameId !== gameId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered));
      return true;
    } catch (error) {
      console.error('Failed to delete reversi game:', error);
      return false;
    }
  }

  /** Clear all saved games. */
  clearAllGames(): boolean {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.AUTO_SAVE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to clear all reversi games:', error);
      return false;
    }
  }

  /** Sync saved games from cloud storage. */
  async syncFromCloud(): Promise<boolean> {
    try {
      if (!this.syncService) return false;
      console.warn('Cloud sync not yet implemented for reversi games');
      return false;
    } catch (error) {
      console.error('Failed to sync reversi games from cloud:', error);
      return false;
    }
  }

  /** Sync saved games to cloud storage. */
  async syncToCloud(): Promise<boolean> {
    try {
      if (!this.syncService) return false;
      console.warn('Cloud sync not yet implemented for reversi games');
      return true;
    } catch (error) {
      console.error('Failed to sync reversi games to cloud:', error);
      return false;
    }
  }
}
